import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
  TrendingUp, Headphones, ListMusic, Users, Target,
  Database, Clock, ArrowRight, CheckCircle2,
  Upload, GitBranch, Lightbulb, MapPin, Download,
  Music, BarChart3, Globe, Calendar, Layers
} from 'lucide-react';

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const STATS = [
  { value: "$30B+", label: "Global Streaming Market", Icon: TrendingUp },
  { value: "165M-200M", label: "Instrumental / Focus Listeners", Icon: Headphones },
  { value: "5,000-10,000", label: "Relevant Playlists", Icon: ListMusic },
  { value: "600M+", label: "Spotify Users", Icon: Users },
];

const TIERS = [
  { tier: "Tier 1", title: "Global Scale", color: "#D4A843", listeners: "3M\u201310M monthly", streams: "15M\u201380M monthly", examples: "Einaudi, Yiruma, \u00d3lafur Arnalds" },
  { tier: "Tier 2", title: "Playlist-Driven", color: "#A0AAB5", listeners: "500K\u20132M monthly", streams: "2M\u201315M monthly", examples: "Years 2\u20133 Target" },
  { tier: "Tier 3", title: "Emerging", color: "#666666", listeners: "10K\u2013200K monthly", streams: "20K\u20131M monthly", examples: "Year 1 Position" },
];

const FLOW_STEPS = [
  { label: "CATALOG", Icon: Database },
  { label: "PLAYLISTS", Icon: ListMusic },
  { label: "TIME", Icon: Clock },
  { label: "REVENUE", Icon: TrendingUp },
];

const MODEL_CARDS = [
  { title: "Catalog", desc: "Every track is a permanent revenue-generating asset.", Icon: Database },
  { title: "Playlists", desc: "Distribution through thousands of independent and editorial playlists.", Icon: ListMusic },
  { title: "Time", desc: "Streams compound continuously once placed.", Icon: Clock },
];

const PRODUCTION = [
  "Hook within 5\u201310 seconds",
  "Optimized for 30-second stream qualification",
  "Emotional, low cognitive load",
  "Loop-friendly structure",
  "Real piano performance (not AI-generated)",
  "1\u20132 tracks released weekly",
];

const COVERS = [
  { num: 1, title: "All of Me", artist: "John Legend" },
  { num: 2, title: "Perfect", artist: "Ed Sheeran" },
  { num: 3, title: "Someone Like You", artist: "Adele" },
  { num: 4, title: "Let Her Go", artist: "Passenger" },
  { num: 5, title: "A Thousand Years", artist: "Christina Perri" },
];

const PLATFORMS = [
  { name: "Spotify", role: "Growth Engine", pct: 70, color: "#1DB954" },
  { name: "Apple Music", role: "Premium Revenue", pct: 15, color: "#FC3C44" },
  { name: "Amazon Music", role: "Underrated Monetization", pct: 10, color: "#25D1DA" },
  { name: "YouTube", role: "Exposure + Education", pct: 5, color: "#FF0000" },
];

const LAYERS = [
  { name: "Master Royalties", rate: "$0.002", desc: "Sound recording royalty via DistroKid" },
  { name: "Mechanical Royalties", rate: "$0.0008", desc: "Composition royalty via MLC/Songtrust" },
  { name: "PRO Royalties", rate: "$0.0002\u2013$0.0005", desc: "Performance royalty via ASCAP/BMI" },
];

const SIGNALS = [
  { signal: "Retention", desc: "30-sec threshold + completion" },
  { signal: "Save Rate", desc: "Listeners adding to library" },
  { signal: "Skip Rate", desc: "Low abandonment signals quality" },
  { signal: "Repeat Plays", desc: "Same listener returning" },
];

const ALGO_STAGES = [
  { stage: 1, title: "Initial Test", days: "Days 1\u20137" },
  { stage: 2, title: "Radio Expansion", days: "Days 7\u201314" },
  { stage: 3, title: "Algorithmic Playlists", days: "Days 14\u201330" },
  { stage: 4, title: "Scale", days: "Day 30+" },
];

const GEO = [
  { region: "US / UK / Germany", label: "Highest Revenue", primary: true },
  { region: "Europe (Rest)", label: "Strong Monetization", primary: false },
  { region: "Asia", label: "High Volume, Lower Payout", primary: false },
];

const TIMELINE = [
  { phase: 1, title: "Foundation", period: "April\u2013June 2026", tracks: "12\u201318", streams: "35K\u201350K", desc: "Train algorithm, establish baseline" },
  { phase: 2, title: "Traction", period: "July\u2013Sept 2026", tracks: "30\u201340", streams: "120K\u2013200K", desc: "Identify winning patterns" },
  { phase: 3, title: "Acceleration", period: "Oct\u2013Dec 2026", tracks: "48\u201360", streams: "390K\u2013500K", desc: "Scale winners, add covers" },
  { phase: 4, title: "Scale", period: "Jan\u2013April 2027", tracks: "72\u2013100", streams: "1,000,000+", desc: "Achieve 1M milestone" },
];

const SCALE_DATA = [
  { tracks: "10 tracks", label: "50K each" },
  { tracks: "30 tracks", label: "10K each" },
  { tracks: "60 tracks", label: "3K each" },
];

const INFRA_FEATURES = [
  { title: "DistroKid Distribution", desc: "All major platforms via a single distribution system", Icon: Upload },
  { title: "Multi-Artist Strategy", desc: "Multiple artist identities to expand editorial access", Icon: Users },
  { title: "Parallel Releases", desc: "Releases across multiple profiles simultaneously", Icon: GitBranch },
];

const FOUNDERS = [
  { name: "Dan Leitner", role: "Growth & Infrastructure", img: "https://customer-assets.emergentagent.com/job_14113597-3129-4f02-b1f3-0db062154d4f/artifacts/u9y054r9_Screenshot%202026-04-09%20at%202.39.18%E2%80%AFPM.png" },
  { name: "Gianni Staiano", role: "Production & Sound Engineering", img: "https://customer-assets.emergentagent.com/job_blink-business-plan/artifacts/irbqq0q9_Gianni%20Photo.jpg" },
];

const HIGHLIGHTS = [
  "Built on real streaming economics",
  "Designed for long-term growth",
  "Driven by consistency, not luck",
];

/* ─── Section Header ─── */
function SectionHeader({ label, title, subtitle }) {
  return (
    <motion.div variants={fadeIn} className="text-center mb-16">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4A843] mb-4">{label}</p>
      <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-[#F5F5F5]">{title}</h2>
      {subtitle && <p className="text-base text-[#A0AAB5] max-w-2xl mx-auto mt-4">{subtitle}</p>}
    </motion.div>
  );
}

/* ─── Flow Arrow ─── */
function FlowArrow() {
  return (
    <>
      <div className="hidden md:flex items-center px-6 text-[#D4A843]/50">
        <div className="w-12 border-t-2 border-dashed border-white/20" />
        <ArrowRight className="w-5 h-5 -ml-1" />
      </div>
      <div className="md:hidden py-2 text-[#D4A843]/50 rotate-90">
        <ArrowRight className="w-5 h-5" />
      </div>
    </>
  );
}

export default function BusinessPlan() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { document.title = "Blink Records | Business Plan"; }, []);

  const handleLogout = async () => {
    navigate('/', { replace: true });
    await logout();
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0D1117] to-[#0A0A0F]" data-testid="business-plan-page">

      {/* ═══ STICKY NAV ═══ */}
      <nav className="bp-nav" data-testid="bp-header">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="text-[#F5F5F5] font-medium text-sm cursor-pointer" onClick={() => navigate('/')} data-testid="bp-logo-link">Blink Records, LLC</span>
          <div className="flex items-center gap-8">
            <button onClick={() => scrollTo('market')} className="hidden md:block text-sm text-[#A0AAB5] hover:text-[#F5F5F5] transition-colors">Market</button>
            <button onClick={() => scrollTo('system')} className="hidden md:block text-sm text-[#A0AAB5] hover:text-[#F5F5F5] transition-colors">Model</button>
            <button onClick={() => scrollTo('revenue')} className="hidden md:block text-sm text-[#A0AAB5] hover:text-[#F5F5F5] transition-colors">Revenue</button>
            <button onClick={() => scrollTo('roadmap')} className="hidden md:block text-sm text-[#A0AAB5] hover:text-[#F5F5F5] transition-colors">Roadmap</button>
            <button onClick={() => scrollTo('team')} className="hidden md:block text-sm text-[#A0AAB5] hover:text-[#F5F5F5] transition-colors">Team</button>
            <span className="hidden md:block text-xs text-[#A0AAB5]/60" data-testid="bp-user-name">{user?.name || user?.email}</span>
            <button onClick={handleLogout} className="text-xs font-medium text-[#D4A843] border border-[#D4A843] rounded-lg px-4 py-2 hover:bg-[#D4A843] hover:text-[#0A0A0F] transition-all duration-300" data-testid="bp-logout-button">Sign Out</button>
          </div>
        </div>
      </nav>

      <main>
        {/* ═══ HERO ═══ */}
        <section id="hero" className="relative min-h-screen flex flex-col justify-center items-center text-center px-6 overflow-hidden" data-testid="bp-hero">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4A843]/10 blur-[120px] rounded-full pointer-events-none" />
          <motion.div className="relative z-10 max-w-4xl mx-auto" initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeIn} className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4A843] mb-6">Blink Records, LLC</motion.p>
            <motion.h1 variants={fadeIn} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter font-medium text-[#F5F5F5] leading-tight mb-8">
              Building Music That<br/><span className="text-[#D4A843]">Pays Over Time</span>
            </motion.h1>
            <motion.p variants={fadeIn} className="text-base md:text-lg font-light text-[#A0AAB5] leading-relaxed max-w-2xl mx-auto mb-4">
              Blink Records is a digital music asset company engineering minimalist cinematic piano compositions for global streaming playlists and long-term royalty compounding.
            </motion.p>
            <motion.p variants={fadeIn} className="text-sm text-[#A0AAB5]/70 mb-12">
              Founded by Dan Leitner &amp; Gianni Staiano &middot; Florida, USA
            </motion.p>
            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button onClick={() => scrollTo('market')} className="bg-[#D4A843] text-[#0A0A0F] font-medium px-8 py-4 rounded-lg hover:bg-[#F5F5F5] transition-all duration-300" data-testid="hero-cta-primary">View the Business Plan</button>
              <button onClick={() => scrollTo('system')} className="border border-white/20 text-[#F5F5F5] px-8 py-4 rounded-lg hover:bg-white/10 transition-all duration-300" data-testid="hero-cta-secondary">Explore the Model</button>
            </motion.div>
          </motion.div>
        </section>

        {/* ═══ MARKET OPPORTUNITY ═══ */}
        <section id="market" className="bp-section-spacing px-6" data-testid="bp-market">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Market Opportunity" title="The Opportunity" />
              <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                {STATS.map((s, i) => (
                  <div key={i} className="glass-card glass-card-hover p-8 gold-border-top" data-testid={`market-stat-${i}`}>
                    <s.Icon className="w-8 h-8 text-[#D4A843] mb-4" />
                    <p className="text-4xl md:text-5xl font-light tracking-tighter text-[#F5F5F5] mb-2">{s.value}</p>
                    <p className="text-sm text-[#A0AAB5]">{s.label}</p>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeIn} className="max-w-3xl mx-auto text-center">
                <p className="text-base md:text-lg font-light text-[#A0AAB5] leading-relaxed">
                  Streaming is no longer driven by artists. It is driven by playlists. Passive listening environments&mdash;focus, study, relaxation&mdash;represent one of the fastest-growing segments in music consumption. Blink Records is built to operate inside that system.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ BENCHMARKS (TIERS) ═══ */}
        <section id="benchmarks" className="bp-section-spacing px-6" data-testid="bp-tiers">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Market Benchmarks" title="Piano & Instrumental Streaming Economics" subtitle="Revenue in this category is driven by catalog scale, playlist penetration, and time — not virality or fame." />
              <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {TIERS.map((t, i) => (
                  <div key={i} className="glass-card glass-card-hover p-6 relative overflow-hidden" data-testid={`benchmark-tier-${i}`}>
                    <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: t.color }} />
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm font-semibold px-2 py-1 rounded" style={{ backgroundColor: `${t.color}20`, color: t.color }}>{t.tier}</span>
                      <span className="text-[#F5F5F5] font-medium">{t.title}</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#A0AAB5]" /><span className="text-sm text-[#A0AAB5]">{t.listeners}</span></div>
                      <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#A0AAB5]" /><span className="text-sm text-[#A0AAB5]">{t.streams}</span></div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-xs text-[#D4A843]">{t.examples}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeIn} className="max-w-3xl mx-auto">
                <div className="glass-card p-8 text-center">
                  <Target className="w-8 h-8 text-[#D4A843] mx-auto mb-4" />
                  <p className="text-base text-[#A0AAB5] mb-4">
                    The gap between tiers is driven by <span className="text-[#F5F5F5]">catalog size</span>, <span className="text-[#F5F5F5]">playlist density</span>, and <span className="text-[#F5F5F5]">time in market</span> &mdash; not viral moments or social followings.
                  </p>
                  <p className="text-sm text-[#D4A843] font-medium">Blink Records is building a system to reach Tier 2 through disciplined execution.</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ THE SYSTEM ═══ */}
        <section id="system" className="bp-section-spacing px-6 bg-[#0A0A0F]/50" data-testid="bp-model">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Business Model" title="The System" />
              {/* Flow */}
              <motion.div variants={fadeIn} className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0 mb-16">
                {FLOW_STEPS.map((step, i) => (
                  <div key={step.label} className="flex items-center">
                    <div className="flex flex-col items-center" data-testid={`system-step-${i}`}>
                      <div className="w-20 h-20 rounded-lg glass-card flex items-center justify-center mb-3 gold-glow">
                        <step.Icon className="w-8 h-8 text-[#D4A843]" />
                      </div>
                      <span className="text-sm font-medium text-[#F5F5F5] tracking-wider">{step.label}</span>
                    </div>
                    {i < FLOW_STEPS.length - 1 && <FlowArrow />}
                  </div>
                ))}
              </motion.div>
              {/* Detail cards */}
              <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {MODEL_CARDS.map((c, i) => (
                  <div key={i} className="glass-card glass-card-hover p-8">
                    <c.Icon className="w-8 h-8 text-[#D4A843] mb-4" />
                    <h3 className="text-lg font-medium text-[#F5F5F5] mb-2">{c.title}</h3>
                    <p className="text-sm text-[#A0AAB5]">{c.desc}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ CONTENT STRATEGY ═══ */}
        <section id="content" className="bp-section-spacing px-6" data-testid="bp-content">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Content Strategy" title="What We Create" />
              <motion.div variants={fadeIn} className="max-w-3xl mx-auto text-center mb-12">
                <p className="text-xl md:text-2xl font-light text-[#F5F5F5] leading-relaxed mb-4">Minimalist cinematic piano compositions designed for passive listening environments.</p>
                <p className="text-base text-[#A0AAB5] leading-relaxed">Built for retention, not attention. Every track is engineered to work <em>with</em> what the listener is doing, not against it. Focus, study, work, relaxation&mdash;our music serves the moment.</p>
              </motion.div>
              <motion.div variants={fadeIn} className="glass-card p-8 max-w-3xl mx-auto mb-8">
                <h4 className="text-lg font-medium text-[#F5F5F5] mb-6">Production Standards</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {PRODUCTION.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="w-4 h-4 text-[#D4A843] flex-shrink-0" />
                      <span className="text-sm text-[#A0AAB5]">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
              <motion.div variants={fadeIn} className="max-w-3xl mx-auto">
                <div className="glass-card p-6 flex gap-4 items-start">
                  <Lightbulb className="w-6 h-6 text-[#D4A843] flex-shrink-0 mt-1" />
                  <p className="text-[#A0AAB5] italic">&ldquo;A stream is counted at ~30 seconds. Every composition is engineered to reach and retain that threshold.&rdquo;</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ GROWTH STRATEGY (COVERS) ═══ */}
        <section id="covers" className="bp-section-spacing px-6 bg-[#0A0A0F]/50" data-testid="bp-covers">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Growth Strategy" title="Strategic Cover Song Deployment" subtitle="Covers drive listener acquisition. Originals build long-term value." />
              <motion.div variants={fadeIn} className="max-w-3xl mx-auto text-center mb-12">
                <p className="text-base text-[#A0AAB5]">Cover releases begin only after 10 original compositions are live. This ensures established sound identity before external traffic.</p>
              </motion.div>
              <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-3xl mx-auto">
                {["1 cover every 2\u20133 weeks", "10 strategic covers total", "4\u20136 month rollout duration"].map((s, i) => (
                  <div key={i} className="glass-card p-6 text-center">
                    <p className="text-sm font-medium text-[#F5F5F5]">{s}</p>
                  </div>
                ))}
              </motion.div>
              {/* Cover list */}
              <motion.div variants={fadeIn} className="glass-card p-8 max-w-3xl mx-auto mb-8">
                <h4 className="text-lg font-medium text-[#F5F5F5] mb-6">Initial Portfolio</h4>
                <div className="space-y-3">
                  {COVERS.map(c => (
                    <div key={c.num} className="flex items-center gap-4 py-2 border-b border-white/5 last:border-0">
                      <span className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center text-xs text-[#A0AAB5] flex-shrink-0">{c.num}</span>
                      <span className="text-sm font-medium text-[#F5F5F5]">{c.title}</span>
                      <span className="text-sm text-[#A0AAB5] ml-auto">{c.artist}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#A0AAB5] italic mt-4">+ 5 more strategic selections</p>
              </motion.div>
              {/* Copyright */}
              <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-8">
                <div className="glass-card p-6">
                  <p className="text-xs text-[#A0AAB5] mb-1">Master Recording</p>
                  <p className="text-sm font-medium text-[#F5F5F5]">100% Blink Records</p>
                </div>
                <div className="glass-card p-6">
                  <p className="text-xs text-[#A0AAB5] mb-1">Composition Rights</p>
                  <p className="text-sm font-medium text-[#F5F5F5]">Original songwriters</p>
                </div>
              </motion.div>
              {/* Licensing */}
              <motion.div variants={fadeIn} className="max-w-3xl mx-auto">
                <div className="glass-card p-6 flex gap-4 items-start">
                  <Lightbulb className="w-6 h-6 text-[#D4A843] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-sm font-medium text-[#F5F5F5] mb-1">Mechanical Licensing</h4>
                    <p className="text-sm text-[#A0AAB5]">All covers licensed via DistroKid or Harry Fox Agency prior to release. Full legal compliance on every track.</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ DISTRIBUTION — WHERE GROWTH COMES FROM ═══ */}
        <section id="platforms" className="bp-section-spacing px-6" data-testid="bp-distribution">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Distribution Strategy" title="Where Growth Comes From" />
              <motion.div variants={fadeIn} className="max-w-3xl mx-auto space-y-6 mb-12">
                {PLATFORMS.map(p => (
                  <div key={p.name} className="flex items-center gap-4">
                    <div className="min-w-[140px]">
                      <p className="text-sm font-medium text-[#F5F5F5]">{p.name}</p>
                      <p className="text-xs text-[#A0AAB5]">{p.role}</p>
                    </div>
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${p.pct}%`, backgroundColor: p.color }} />
                    </div>
                    <span className="text-sm font-semibold text-[#F5F5F5] min-w-[40px] text-right">{p.pct}%</span>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeIn} className="text-center">
                <p className="text-base text-[#A0AAB5]">Spotify grows the catalog. <span className="text-[#F5F5F5]">Apple and Amazon monetize it.</span></p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ FINANCIAL MODEL ═══ */}
        <section id="revenue" className="bp-section-spacing px-6 bg-[#0A0A0F]/50" data-testid="bp-financial">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Financial Model" title="The Economics" />
              <motion.div variants={fadeIn} className="text-center mb-12">
                <p className="text-base text-[#A0AAB5]">Blended Base Rate: <span className="text-[#F5F5F5] font-medium">~$0.002 per stream</span></p>
              </motion.div>
              {/* Revenue cards */}
              <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-12">
                {[
                  { streams: "100K", amount: "$200" },
                  { streams: "500K", amount: "$1,000" },
                  { streams: "1M", amount: "$2,000", highlight: true },
                ].map((r, i) => (
                  <div key={i} className={`glass-card p-8 text-center ${r.highlight ? 'gold-glow' : ''}`}>
                    <p className="text-3xl font-light tracking-tighter text-[#F5F5F5] mb-1">{r.streams}</p>
                    <p className="text-xs text-[#A0AAB5] mb-3">Streams</p>
                    <p className="text-xl font-medium text-[#D4A843]">{r.amount}</p>
                  </div>
                ))}
              </motion.div>
              {/* Stacked Layers */}
              <motion.div variants={fadeIn} className="glass-card p-8 max-w-3xl mx-auto mb-8">
                <h4 className="text-lg font-medium text-[#F5F5F5] mb-6">Stacked Revenue Layers</h4>
                <div className="space-y-4">
                  {LAYERS.map((l, i) => (
                    <div key={i} className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 py-3 border-b border-white/5 last:border-0">
                      <span className="text-sm font-medium text-[#F5F5F5] md:min-w-[180px]">{l.name}</span>
                      <span className="text-sm font-mono text-[#D4A843]">{l.rate}</span>
                      <span className="text-sm text-[#A0AAB5]">{l.desc}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-white/10 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <span className="text-sm font-medium text-[#F5F5F5] md:min-w-[180px]">Combined Per-Stream Value</span>
                  <span className="text-sm font-mono text-[#D4A843]">~$0.003&ndash;$0.0035</span>
                  <span className="text-sm text-[#A0AAB5]">when fully captured</span>
                </div>
              </motion.div>
              <motion.div variants={fadeIn} className="max-w-3xl mx-auto">
                <div className="glass-card p-6 flex gap-4 items-start">
                  <Lightbulb className="w-6 h-6 text-[#D4A843] flex-shrink-0 mt-1" />
                  <p className="text-[#A0AAB5] italic">&ldquo;Most independent artists collect only one layer. Blink Records captures all three.&rdquo;</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ ALGORITHMIC GROWTH ═══ */}
        <section id="algorithm" className="bp-section-spacing px-6" data-testid="bp-algorithm">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Growth Engine" title="Algorithmic Growth Strategy" subtitle="Spotify promotes songs with strong listener signals. Every track is engineered to pass algorithm tests." />
              <motion.div variants={fadeIn} className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto mb-12">
                {SIGNALS.map((s, i) => (
                  <div key={i} className="glass-card p-6 text-center">
                    <p className="text-sm font-semibold text-[#F5F5F5] mb-1">{s.signal}</p>
                    <p className="text-xs text-[#A0AAB5]">{s.desc}</p>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeIn} className="glass-card p-8 max-w-3xl mx-auto mb-8">
                <h4 className="text-lg font-medium text-[#F5F5F5] mb-6">Algorithm Growth Stages</h4>
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  {ALGO_STAGES.map((s, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="flex flex-col items-center text-center">
                        <span className="text-xs font-semibold px-2 py-1 rounded mb-2" style={{ backgroundColor: 'rgba(212,168,67,0.1)', color: '#D4A843' }}>Stage {s.stage}</span>
                        <span className="text-sm font-medium text-[#F5F5F5]">{s.title}</span>
                        <span className="text-xs text-[#A0AAB5]">{s.days}</span>
                      </div>
                      {i < ALGO_STAGES.length - 1 && <ArrowRight className="hidden md:block w-4 h-4 text-[#A0AAB5]/40" />}
                    </div>
                  ))}
                </div>
              </motion.div>
              <motion.div variants={fadeIn} className="text-center">
                <p className="text-base text-[#A0AAB5]">First 10&ndash;15 seconds must hook the listener. <span className="text-[#F5F5F5]">No slow intros. Immediate emotional identity.</span></p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ GEOGRAPHIC STRATEGY ═══ */}
        <section id="geo" className="bp-section-spacing px-6 bg-[#0A0A0F]/50" data-testid="bp-geo">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Geographic Strategy" title="Where Value Comes From" />
              <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-8">
                {GEO.map((g, i) => (
                  <div key={i} className={`glass-card p-6 text-center ${g.primary ? 'gold-glow' : ''}`}>
                    <p className="text-lg font-medium text-[#F5F5F5] mb-1">{g.region}</p>
                    <p className="text-sm text-[#D4A843]">{g.label}</p>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeIn} className="text-center">
                <p className="text-base text-[#A0AAB5]">1 US stream can be worth <span className="text-[#F5F5F5]">multiple streams</span> in lower-tier regions.</p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ ROADMAP ═══ */}
        <section id="roadmap" className="bp-section-spacing px-6" data-testid="bp-timeline">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Execution Timeline" title="12-Month Roadmap" subtitle="April 1, 2026 → April 1, 2027" />
              <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {TIMELINE.map((t, i) => (
                  <div key={i} className="glass-card glass-card-hover p-6 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-[#D4A843]" style={{ opacity: 0.3 + (i * 0.2) }} />
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#D4A843] mb-2">Phase {t.phase}</p>
                    <h4 className="text-lg font-medium text-[#F5F5F5] mb-1">{t.title}</h4>
                    <p className="text-sm text-[#A0AAB5] mb-4">{t.period}</p>
                    <div className="space-y-1 mb-3">
                      <p className="text-xs text-[#A0AAB5]">Tracks: <span className="text-[#F5F5F5]">{t.tracks}</span></p>
                      <p className="text-xs text-[#A0AAB5]">Streams: <span className="text-[#F5F5F5]">{t.streams}</span></p>
                    </div>
                    <p className="text-xs text-[#A0AAB5] italic">{t.desc}</p>
                  </div>
                ))}
              </motion.div>
              {/* Milestone */}
              <motion.div variants={fadeIn} className="text-center mb-12">
                <div className="inline-block glass-card px-8 py-6 gold-glow">
                  <p className="text-4xl font-light text-[#F5F5F5]">~<span className="text-[#D4A843]">1M</span> streams</p>
                  <p className="text-sm text-[#A0AAB5] mt-2">Target: Q1 2027</p>
                </div>
              </motion.div>
              {/* 1M Celebration */}
              <motion.div variants={fadeIn} className="glass-card p-6 max-w-2xl mx-auto">
                <div className="flex items-center gap-3 mb-3">
                  <MapPin className="w-5 h-5 text-[#D4A843]" />
                  <h4 className="font-medium text-[#F5F5F5]">1M Milestone Celebration</h4>
                </div>
                <p className="text-sm text-[#A0AAB5] mb-3">Upon hitting 1M streams: celebration trip with content capture.</p>
                <div className="flex flex-wrap gap-2">
                  {["New Orleans", "Nashville", "Piano performances", "Brand content"].map(tag => (
                    <span key={tag} className="text-xs bg-white/5 text-[#A0AAB5] px-3 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ SCALE / PORTFOLIO ═══ */}
        <section id="scale" className="bp-section-spacing px-6 bg-[#0A0A0F]/50" data-testid="bp-portfolio">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Growth Model" title="How Scale Happens" />
              <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-12">
                {SCALE_DATA.map((s, i) => (
                  <div key={i} className="glass-card p-6 text-center">
                    <p className="text-2xl font-light text-[#F5F5F5] mb-1">{s.tracks}</p>
                    <p className="text-sm text-[#A0AAB5] mb-3">&times;</p>
                    <p className="text-xl text-[#D4A843]">{s.label}</p>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeIn} className="text-center mb-8">
                <div className="inline-block glass-card px-8 py-6 gold-glow">
                  <p className="text-sm text-[#A0AAB5] mb-2">Total</p>
                  <p className="text-4xl font-light text-[#F5F5F5]">~<span className="text-[#D4A843]">1M</span> streams</p>
                </div>
              </motion.div>
              <motion.div variants={fadeIn} className="text-center">
                <p className="text-base text-[#A0AAB5] italic max-w-2xl mx-auto">&ldquo;You don't need every track to win. You need enough volume for winners to emerge.&rdquo;</p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ DISTRIBUTION INFRASTRUCTURE ═══ */}
        <section id="distribution" className="bp-section-spacing px-6" data-testid="bp-infra">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Infrastructure" title="Distribution Infrastructure" />
              <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {INFRA_FEATURES.map((f, i) => (
                  <div key={i} className="glass-card glass-card-hover p-8" data-testid={`distribution-feature-${i}`}>
                    <f.Icon className="w-8 h-8 text-[#D4A843] mb-4" />
                    <h3 className="text-lg font-medium text-[#F5F5F5] mb-2">{f.title}</h3>
                    <p className="text-sm text-[#A0AAB5]">{f.desc}</p>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeIn} className="max-w-2xl mx-auto">
                <div className="glass-card p-8 flex gap-4">
                  <Lightbulb className="w-6 h-6 text-[#D4A843] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-lg font-medium text-[#F5F5F5] mb-2">Key Insight</h4>
                    <p className="text-[#A0AAB5]">Editorial pitching is limited per profile. Multiple artist identities expand access to Spotify's editorial playlist consideration window.</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ FOUNDERS ═══ */}
        <section id="team" className="bp-section-spacing px-6 bg-[#0A0A0F]/50" data-testid="bp-founders">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <SectionHeader label="Leadership" title="Founders" />
              <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto mb-12">
                {FOUNDERS.map((f, i) => (
                  <div key={i} className="group" data-testid={`founder-${f.name.split(' ')[0].toLowerCase()}`}>
                    <div className="glass-card overflow-hidden">
                      <div className="relative aspect-square overflow-hidden">
                        <img alt={f.name} src={f.img} className="w-full h-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-transparent to-transparent opacity-60" />
                      </div>
                      <div className="p-6 border-t border-white/10 group-hover:border-[#D4A843]/30 transition-colors">
                        <h3 className="text-xl font-medium text-[#F5F5F5] mb-1">{f.name}</h3>
                        <p className="text-sm text-[#A0AAB5]">{f.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeIn} className="text-center">
                <p className="text-lg text-[#A0AAB5] italic">&ldquo;Building a catalog that compounds.&rdquo;</p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ FINAL / SUMMARY ═══ */}
        <section id="final" className="bp-section-spacing px-6" data-testid="bp-summary">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
              <motion.div variants={fadeIn} className="text-center mb-12">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4A843] mb-4">Summary</p>
                <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-[#F5F5F5]">The Bottom Line</h2>
              </motion.div>
              <motion.div variants={fadeIn} className="text-center mb-12">
                <p className="text-2xl md:text-3xl lg:text-4xl font-light text-[#F5F5F5] leading-relaxed max-w-3xl mx-auto">
                  This is not a hit-driven model.<br/><span className="text-[#D4A843]">This is a compounding system.</span>
                </p>
              </motion.div>
              <motion.div variants={fadeIn} className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 mb-12">
                {HIGHLIGHTS.map((h, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4A843]" />
                    <span className="text-[#A0AAB5]">{h}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* ═══ FOOTER ═══ */}
      <footer className="py-8 px-6 border-t border-white/5 text-center" data-testid="bp-footer">
        <p className="text-xs text-[#A0AAB5]/60">&copy; {new Date().getFullYear()} Blink Records, LLC</p>
      </footer>
    </div>
  );
}
